import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import { fetchCompany } from '../../../../states/containers/setting/companySlice'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { format, nowDate, secToHour, secToMinute, secToTwoDigitsHourMinute } from '../../../../utils/date'
import Holidays from 'date-holidays'
import { fetchTeamList } from '../../../../states/containers/team/listSlice'
import { Link, Prompt } from 'react-router-dom'
import { useAlert } from 'react-alert'
import AddMemberGroupingModule from '../../../components/setting/AddMemberGroupingModule'
import {
  createHolidaySchedule,
  destroyHolidaySchedule,
  fetchHolidaySchedule,
  fetchHolidayScheduleDetail,
  updateHolidaySchedule,
} from '../../../../states/containers/holidaySchedule/holidayScheduleSlice'
import LoadingSquare from '../../../components/common/LoadingSquare'
import UserGuide from '../../../components/common/UserGuide'
import { setPath } from '../../../../utils/url'
import { URL } from '../../../constants/url'
import { updateSettingHolidays } from '../../../../states/containers/common/currentSlice'
import ConfirmationModal from '../../../components/setting/holidaySchedule/ConfirmationModal'
import MonthlyCalendar from '../../../components/setting/holidaySchedule/MonthlyCalendar'
import { RequestPatchHolidaySchedule } from '../../../../states/types/holidaySchedule'
import { pickNumber } from '../../../../utils/string'
import Modal from '../../../components/common/Modal'
import { uniqueId } from 'lodash'

interface Item {
  id: number
  title: string | null
  holidayEnabled: boolean
  holidayList: string[]
  memberIds: number[]
  common: boolean
  baseWorkStartTime: string | null
  baseWorkEndTime: string | null
  baseBreakTime: number
  scheduledBreakTimes: {
    startTime: string
    endTime: string
  }[]
  useCommonWorkTime: boolean
  useCommonHolidayList: boolean
  useCommonScheduledBreakTimes: boolean
}

interface Error {
  type: string
  id: number
  message: string
  target?: string[]
}

interface ConfirmationModalState {
  display: boolean,
  type: string,
  name: string,
  callback: VoidFunction,
}

const SELECT_INTERVAL_WORK_TIME_MINUTES = 5

const SELECT_INTERVAL_BREAK_TIME_MINUTES = 15

const BREAK_TIME_LIMIT = 5

const Index: FC = () => {
  const dispatch = useDispatch()
  const alert = useAlert()

  const {
    login,
    teamList,
    holidaySchedule,
    current,
  } = useSelector((state: RootState) => (state))

  const holidays = new Holidays()
  holidays.init('JP')

  const titleRef = useRef<HTMLInputElement>(null)

  const [isEdit, setIsEdit] = useState<boolean>(false)
  const [targetYear, setTargetYear] = useState<number>(Number(nowDate("YYYY")))
  const [selectNumber, setSelectNumber] = useState<number>(0)
  const [isEditTitle, setIsEditTitle] = useState<boolean>(false)

  const [errors, setErrors] = useState<Error[]>([])

  const itemInit: Item = {
    id: 0,
    title: null,
    holidayEnabled: false,
    holidayList: [],
    memberIds: [],
    common: false,
    baseWorkStartTime: null,
    baseWorkEndTime: null,
    baseBreakTime: 0,
    scheduledBreakTimes: [],
    useCommonWorkTime: false,
    useCommonHolidayList: false,
    useCommonScheduledBreakTimes: false,
  }

  const [items, setItems] = useState<Item[]>([])

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isChangeLoading, setIsChangeLoading] = useState<boolean>(false)
  const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false)

  const [confirmationModalState, setConfirmationModalState] = useState<ConfirmationModalState>({
    display: false,
    type: "",
    name: "",
    callback: () => {
      // none
    },
  })

  const [targetTitle, setTargetTitle] = useState<string>("")
  const [targetMemberIds, setTargetMemberIds] = useState<number[]>([])
  const [holidayList, setHolidayList] = useState<string[][]>([])
  const [isSubmit, setIsSubmit] = useState<boolean>(false)
  const [isClear, setIsClear] = useState<boolean>(false)
  const [quickChange, setQuickChange] = useState<string>("")

  const [isBaseWorkTime, setIsBaseWorkTime] = useState<boolean>(false)
  const [baseWorkStartTime, setBaseWorkStartTime] = useState<string>("09:00")
  const [baseWorkEndTime, setBaseWorkEndTime] = useState<string>("18:00")
  const [baseBreakTime, setBaseBreakTime] = useState<number>(0)
  const [isScheduledBreakTimes, setIsScheduledBreakTimes] = useState<boolean>(false)
  const [scheduledBreakTimes, setScheduledBreakTimes] = useState<{startTime: string, endTime: string}[]>([])
  const [isHoliday, setIsHoliday] = useState<boolean>(true)
  const [modalType, setModalType] = useState<string>("")

  const [isUseCommonWorkTime, setIsUseCommonWorkTime] = useState<boolean>(false)
  const [isUseCommonHolidayList, setIsUseCommonHolidayList] = useState<boolean>(false)
  const [isUseCommonScheduledBreakTimes, setIsUseCommonScheduledBreakTimes] = useState<boolean>(false)
  const [isDisplayInfo, setIsDisplayInfo] = useState<boolean>(false)

  const [addedHolidays, setAddedHolidays] = useState<string[][]>([])
  const [removedHolidays, setRemovedHolidays] = useState<string[][]>([])

  const workTimeList = useMemo(() => {
    if (SELECT_INTERVAL_WORK_TIME_MINUTES > 60) return []
    const tmp = Array(1440 / SELECT_INTERVAL_WORK_TIME_MINUTES).fill(null).map((_v, i) => (
      secToTwoDigitsHourMinute(60 * (i * SELECT_INTERVAL_WORK_TIME_MINUTES) + (3600 * 5))
    ))
    return tmp
  }, [])

  const breakTimeList = useMemo(() => {
    if (SELECT_INTERVAL_BREAK_TIME_MINUTES > 60) return []
    const tmp = Array((960 + SELECT_INTERVAL_BREAK_TIME_MINUTES) / SELECT_INTERVAL_BREAK_TIME_MINUTES).fill(null).map((_v, i) => {
      const hours = secToHour(60 * (i * SELECT_INTERVAL_BREAK_TIME_MINUTES))
      const minutes = secToMinute(60 * (i * SELECT_INTERVAL_BREAK_TIME_MINUTES))
      return ({
        key: `${hours ? `${hours}時間` : ""}${minutes ? `${minutes}分` : ""}`,
        value: 60 * (i * SELECT_INTERVAL_BREAK_TIME_MINUTES),
      })
    })
    return tmp
  }, [])

  const workTimeSeconds = useMemo(() => {
    if (!baseWorkStartTime || !baseWorkEndTime) return 0
    const [tmpStartHours, tmpStartMinutes] = baseWorkStartTime.split(":")
    const [tmpEndHours, tmpEndMinutes] = baseWorkEndTime.split(":")
    return ((Number(tmpEndHours) - Number(tmpStartHours)) * 3600) + ((Number(tmpEndMinutes) - Number(tmpStartMinutes)) * 60)
  }, [baseWorkStartTime, baseWorkEndTime])

  const scheduledBreakTimeSeconds = useMemo(() => {
    if (!scheduledBreakTimes.length) return 0
    return scheduledBreakTimes.reduce((s, v) => {
      const [tmpStartHours, tmpStartMinutes] = v.startTime.split(":")
      const [tmpEndHours, tmpEndMinutes] = v.endTime.split(":")
      return s + ((Number(tmpEndHours) - Number(tmpStartHours)) * 3600) + ((Number(tmpEndMinutes) - Number(tmpStartMinutes)) * 60)
    }, 0)
  }, [scheduledBreakTimes])

  const buildSecToTime = (sec: number) => {
    let tmpText = ""
    if (Number(secToHour(sec))) {
      tmpText += `${secToHour(sec)}時間`
    }
    if (Number(secToMinute(sec))) {
      tmpText += `${secToMinute(sec)}分`
    } else {
      tmpText += "0分"
    }
    return tmpText
  }

  const addCondition = () => {
    let isConfirm = false
    if (isEdit) {
      // eslint-disable-next-line no-alert
      const confirm = window.confirm("変更内容が保存されていません。\n休日グループを作成すると変更内容がリセットされます。\n本当によろしいですか？")
      if (confirm) {
        isConfirm = true
      }
    } else {
      isConfirm = true
    }
    if (isConfirm) {
      setIsLoading(true)
      dispatch(createHolidaySchedule(
        { name: `設定(${nowDate("YYYY-MM-DD HH:mm:ss")})` },
        (result) => {
          setItems(
            [
              ...items,
              {
                ...result.holiday_schedule,
                id: result.holiday_schedule.id,
                title: result.holiday_schedule.name,
                memberIds: result.holiday_schedule.member_ids,
                holidayEnabled: result.holiday_schedule.holiday_enabled,
                holidayList: result.holiday_schedule.holidays,
                baseWorkStartTime: result.holiday_schedule.base_work_start_time,
                baseWorkEndTime: result.holiday_schedule.base_work_end_time,
                baseBreakTime: result.holiday_schedule.base_break_time,
                scheduledBreakTimes: result.holiday_schedule.scheduled_break_times.map((v) => ({ startTime: v.start_time, endTime: v.end_time })),
                useCommonHolidayList: result.holiday_schedule.use_common_holiday_list,
                useCommonWorkTime: result.holiday_schedule.use_common_work_time,
                useCommonScheduledBreakTimes: result.holiday_schedule.use_common_scheduled_break_times,
              },
            ],
          )
          alert.success(`${result.holiday_schedule.name}を追加しました`)
          setIsEdit(false)
          setSelectNumber(result.holiday_schedule.id)
        },
      ))
    }
  }

  const delCondition = (num: number) => {
    setIsLoading(true)
    const deleteItem = items.find((v) => v.id === num)
    dispatch(destroyHolidaySchedule(
      { id: num.toString() },
      () => {
        setItems(items.filter((v) => v.id !== num))
        alert.success(`${deleteItem?.title}を削除しました`)
        setSelectNumber(items.find((v) => v.common)?.id || 0)
        setIsEdit(false)
      },
    ))
  }

  const changeSelectNumber = (num: number) => {
    let isConfirm = false
    if (isEdit) {
      // eslint-disable-next-line no-alert
      const confirm = window.confirm("変更内容が保存されていません。\nグループを移動すると変更内容がリセットされます。\n本当によろしいですか？")
      if (confirm) {
        isConfirm = true
      }
    } else {
      isConfirm = true
    }
    if (isConfirm) {
      setSelectNumber(num)
      setIsEditTitle(false)
      setIsChangeLoading(true)
      setIsEdit(false)
    }
  }

  const selectMemberIds = (memberIds: number[]) => {
    setTargetMemberIds(memberIds)
    setIsEdit(true)
  }

  const ejectMemberId = (memberId: number) => {
    setTargetMemberIds((prev) => prev.filter((v) => v !== memberId))
    setIsEdit(true)
  }

  const saveTitle = () => {
    setTargetTitle(titleRef.current?.value || `設定(${nowDate("YYYY-MM-DD HH:mm:ss")})`)
    setIsEditTitle(false)
    setIsEdit(true)
  }

  const changeHoliday = (addList: string[], removeList: string[]) => {
    setAddedHolidays((cur) => [...cur, addList])
    setRemovedHolidays((cur) => [...cur, removeList])
  }

  const clickQuickHolidaySetting = (type: string) => {
    setQuickChange(type)
    setIsEdit(true)
  }

  const clearNotWorkdayList = () => {
    setIsClear(true)
    setIsEdit(true)
  }

  const copyCommonHolidays = () => {
    const commonHolidays = items.find((v) => v.common)?.holidayList
    if (!commonHolidays) return
    setHolidayList(
      Array(12).fill(null).map((_v, i) => (
        commonHolidays.filter((w) => Number(format(w, "M")) === i + 1)
      )),
    )
    setIsEdit(true)
  }

  const buildQuickHolidaySettingBox = () => (
    <div
      style={{
        border: "solid 1px #CCCCCC",
        borderRadius: "4px",
        marginBottom: "16px",
        backgroundColor: "#FFFFFF",
        padding: "8px",
      }}
    >
      <div
        style={{
          marginBottom: "8px",
        }}
      >
        クイック選択
      </div>
      <div
        style={{
          display: "flex",
          flexFlow: "column",
          gap: "8px",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            onClick={() => clickQuickHolidaySetting("sss")}
            style={{
              border: "solid 1px #CCCCCC",
              borderRadius: "4px",
              backgroundColor: "#fff1f1",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              backgroundImage: "linear-gradient(135deg, rgb(255 241 241), rgb(255 241 241) 20%, rgb(230, 245, 255))",
            }}
          >
            {`${targetYear}年の土日祝`}
          </button>
          <button
            type="button"
            onClick={() => clickQuickHolidaySetting("sun")}
            style={{
              border: "solid 1px #CCCCCC",
              borderRadius: "4px",
              backgroundColor: "#fff1f1",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            {`${targetYear}年の日曜日`}
          </button>
          <button
            type="button"
            onClick={() => clickQuickHolidaySetting("mon")}
            style={{
              border: "solid 1px #CCCCCC",
              borderRadius: "4px",
              backgroundColor: "#ffffff",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            {`${targetYear}年の月曜日`}
          </button>
          <button
            type="button"
            onClick={() => clickQuickHolidaySetting("tue")}
            style={{
              border: "solid 1px #CCCCCC",
              borderRadius: "4px",
              backgroundColor: "#ffffff",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            {`${targetYear}年の火曜日`}
          </button>
          <button
            type="button"
            onClick={() => clickQuickHolidaySetting("wed")}
            style={{
              border: "solid 1px #CCCCCC",
              borderRadius: "4px",
              backgroundColor: "#ffffff",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            {`${targetYear}年の水曜日`}
          </button>
          <button
            type="button"
            onClick={() => clickQuickHolidaySetting("thu")}
            style={{
              border: "solid 1px #CCCCCC",
              borderRadius: "4px",
              backgroundColor: "#ffffff",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            {`${targetYear}年の木曜日`}
          </button>
          <button
            type="button"
            onClick={() => clickQuickHolidaySetting("fri")}
            style={{
              border: "solid 1px #CCCCCC",
              borderRadius: "4px",
              backgroundColor: "#ffffff",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            {`${targetYear}年の金曜日`}
          </button>
          <button
            type="button"
            onClick={() => clickQuickHolidaySetting("sat")}
            style={{
              border: "solid 1px #CCCCCC",
              borderRadius: "4px",
              backgroundColor: "#e6f5ff",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            {`${targetYear}年の土曜日`}
          </button>
          <button
            type="button"
            onClick={() => clickQuickHolidaySetting("holiday")}
            style={{
              border: "solid 1px #CCCCCC",
              borderRadius: "4px",
              backgroundColor: "#fff1f1",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            {`${targetYear}年の祝日`}
          </button>
        </div>
      </div>
    </div>
  )

  const saveSetting = () => {
    setIsSubmitLoading(true)
    setIsChangeLoading(true)
    setIsSubmit(true)
  }

  const changeTargetYear = (year: number) => {
    setTargetYear(year)
  }

  const openConfirmationModal = (id: number, name: string) => {
    setConfirmationModalState({
      ...confirmationModalState,
      display: true,
      type: "delete",
      name: name,
      callback: () => delCondition(id),
    })
  }

  const closeConfirmationModal = () => {
    setConfirmationModalState({
      ...confirmationModalState,
      display: false,
      type: "",
      name: "",
      callback: () => {
        // none
      },
    })
  }

  const changeValue = (changeCallback: (value: string | number) => void, value: string | number) => {
    changeCallback(value)
    setIsEdit(true)
  }

  const buildWorkTimeSelectBox = (currentValue: string, changeCallback: (value: string) => void) => {
    if (!currentValue) {
      return (<>指定なし</>)
    }
    return (
      <select
        style={{
          border: "solid 1px #CCCCCC",
          borderRadius: "4px",
          padding: "4px",
          backgroundColor: "#FFFFFF",
          appearance: "auto",
        }}
        value={currentValue}
        onChange={(e) => changeValue(changeCallback, e.target.value)}
      >
        {
          workTimeList.map((v) => (
            <option value={v} key={`${v}`}>
              {v || "指定なし"}
            </option>
          ))
        }
      </select>
    )
  }

  const buildBreakTimeSelectBox = (currentValue: number, changeCallback: (value: number) => void) => (
    <select
      style={{
        border: "solid 1px #CCCCCC",
        borderRadius: "4px",
        padding: "4px",
        backgroundColor: "#FFFFFF",
        appearance: "auto",
      }}
      value={currentValue}
      onChange={(e) => changeValue(changeCallback, Number(e.target.value))}
    >
      {
        breakTimeList.map((v) => (
          <option value={v.value} key={`break_${v.value}`}>
            {v.key || "指定なし"}
          </option>
        ))
      }
    </select>
  )

  const buildCheckBox = (value: boolean) => (
    value ? (
      <FontAwesomeIcon
        icon={["fas", "check-square"]}
        style={{ color: "#008800" }}
        fixedWidth
      />
    ) : (
      <FontAwesomeIcon
        icon={["far", "square"]}
        style={{ color: "#999999" }}
        fixedWidth
      />
    )
  )

  const timeAdjuster = (time: string | null, to29 = false) => {
    if (to29) {
      if (time) {
        const [hours, minutes] = time.split(":")
        if (Number(hours) >= 5) {
          return `0${time}`.slice(-5)
        }
        return `0${Number(hours) + 24}:${minutes}`.slice(-5)
      }
    }
    if (time) {
      const [hours, minutes] = time.split(":")
      if (Number(hours) < 24) {
        return `0${time}`.slice(-5)
      }
      return `0${Number(hours) - 24}:${minutes}`.slice(-5)
    }
    return null
  }

  const changeSettingBaseWorkTime = () => {
    if (isBaseWorkTime) {
      setBaseWorkStartTime("")
      setBaseWorkEndTime("")
      setBaseBreakTime(0)
    } else {
      const common = items.find((v) => v.common)
      setBaseWorkStartTime(timeAdjuster(common?.baseWorkStartTime || null, true) || "09:00")
      setBaseWorkEndTime(timeAdjuster(common?.baseWorkEndTime || null, true) || "18:00")
      setBaseBreakTime(common?.baseBreakTime || 3600)
      setIsDisplayInfo(true)
      setModalType("baseWorkTime")
    }
    setIsEdit(true)
    setIsBaseWorkTime((v) => !v)
  }

  const changeSettingScheduledBreakTime = () => {
    if (isScheduledBreakTimes) {
      setScheduledBreakTimes([{ startTime: "", endTime: "" }])
    } else {
      const common = items.find((v) => v.common)
      setScheduledBreakTimes(
        common?.scheduledBreakTimes.length
          ? common?.scheduledBreakTimes.map((v) => ({
            startTime: timeAdjuster(v.startTime, true) || "12:00",
            endTime: timeAdjuster(v.endTime, true) || "13:00",
          }))
          : [{ startTime: "12:00", endTime: "13:00" }],
      )
      setIsDisplayInfo(true)
      setModalType("scheduledBreakTime")
    }
    setIsEdit(true)
    setIsScheduledBreakTimes((v) => !v)
  }

  const changeSettingHoliday = () => {
    if (!isHoliday) {
      setIsDisplayInfo(true)
      setModalType("holiday")
    }
    setIsEdit(true)
    setIsHoliday((v) => !v)
  }

  const addBreakTime = () => {
    let tmpTime = { startTime: "12:00", endTime: "13:00" }
    if (scheduledBreakTimes.length) {
      const targetBreakTime = scheduledBreakTimes[scheduledBreakTimes.length - 1]
      const [newEndHours, newEndMinutes] = targetBreakTime.endTime.split(':')
      tmpTime = {
        startTime: targetBreakTime.endTime,
        endTime: `${Number(newEndHours) < 28 ? `00${Number(newEndHours) + 1}`.slice(-2) : "28"}:${newEndMinutes}`,
      }
    }
    setScheduledBreakTimes((cur) => [...cur, tmpTime])
    setIsEdit(true)
  }

  const delBreakTime = (index: number) => {
    if (scheduledBreakTimes.length === 1) {
      setScheduledBreakTimes([{ startTime: "", endTime: "" }])
      setIsScheduledBreakTimes(false)
    } else {
      setScheduledBreakTimes((cur) => cur.filter((_v, i) => i !== index))
    }
    setIsEdit(true)
  }

  const closeInfoModal = () => {
    setIsDisplayInfo(false)
  }

  const changeUseCommon = (callback: (v: React.SetStateAction<boolean>) => void) => {
    callback((v) => !v)
    setIsEdit(true)
  }

  useEffect(() => {
    setSelectNumber(0)
    dispatch(fetchHolidaySchedule())
  }, [])

  useEffect(() => {
    if (((addedHolidays.length !== 12 || removedHolidays.length !== 12) && isHoliday) || !isSubmit) return
    const tmpId = selectNumber
    setIsSubmitLoading(true)
    setIsChangeLoading(true)
    const tmpData = items.find((v) => v.id === tmpId)
    if (!tmpData) return
    const params: RequestPatchHolidaySchedule = {
      id: tmpId,
      name: targetTitle,
      member_ids: targetMemberIds,
      scheduled_break_times: isScheduledBreakTimes ? scheduledBreakTimes.map((v) => ({
        start_time: timeAdjuster(v.startTime) || "12:00",
        end_time: timeAdjuster(v.endTime) || "13:00",
      })) : [],
    }
    if (tmpData.baseWorkStartTime !== (baseWorkStartTime || null)) {
      params.base_work_start_time = timeAdjuster(baseWorkStartTime)
    }
    if (tmpData.baseWorkEndTime !== (baseWorkEndTime || null)) {
      params.base_work_end_time = timeAdjuster(baseWorkEndTime)
    }
    if (tmpData.baseBreakTime !== baseBreakTime) {
      params.base_break_time = baseBreakTime
    }
    if (tmpData.useCommonWorkTime !== isUseCommonWorkTime) {
      params.use_common_work_time = isUseCommonWorkTime
    }
    if (tmpData.useCommonHolidayList !== isUseCommonHolidayList) {
      params.use_common_holiday_list = isUseCommonHolidayList
    }
    if (tmpData.useCommonScheduledBreakTimes !== isUseCommonScheduledBreakTimes) {
      params.use_common_scheduled_break_times = isUseCommonScheduledBreakTimes
    }
    if (tmpData.holidayEnabled !== isHoliday) {
      params.holiday_enabled = isHoliday
    }
    if (addedHolidays.flat().length || removedHolidays.flat().length) {
      params.holidays = {}
      if (addedHolidays.flat().length) params.holidays.added_date_list = addedHolidays.flat()
      if (removedHolidays.flat().length) params.holidays.removed_date_list = removedHolidays.flat()
    }
    dispatch(updateHolidaySchedule(
      params,
      () => {
        setIsEdit(false)
        setIsSubmit(false)
        setIsSubmitLoading(false)
        setSelectNumber(tmpId)
        dispatch(fetchHolidayScheduleDetail({ id: selectNumber }))
        setTimeout(() => {
          alert.success(`${targetTitle}を更新しました`)
          setAddedHolidays([])
          setRemovedHolidays([])
        }, 500)
      },
    ))
  }, [addedHolidays, removedHolidays, isSubmit])

  useEffect(() => {
    if (!login.accessToken) return
    dispatch(fetchCompany())
  }, [login.accessToken])

  useEffect(() => {
    if (!login.accessToken) return
    if (!teamList.teams.length) {
      dispatch(
        fetchTeamList(() => { /* do nothing */ }),
      )
    }
  }, [login.accessToken])

  useEffect(() => {
    const tmpErrors: Error[] = []
    const targetItem = items.filter((v) => v.id !== selectNumber)
    if (targetItem.map((v) => v.title).includes(targetTitle)) {
      tmpErrors.push({ type: "title", id: selectNumber, message: "設定名が他の休日設定と重複しています。設定名は重複しないようにしてください。" })
    }
    if (targetItem.map((v) => v.memberIds).flat().some((v) => targetMemberIds.includes(v))) {
      const tmpMemberIds = Array.from(targetItem.map((v) => v.memberIds).flat()).filter((v) => targetMemberIds.includes(v))
      const memberNames = teamList.full_members.filter((member) => tmpMemberIds.includes(member.id)).map((v) => v.name)
      tmpErrors.push({
        type: "member",
        id: selectNumber,
        message: "対象メンバーが他の休日設定の対象になっています。メンバーひとりに設定できる休日設定は一つです。",
        target: memberNames,
      })
    }
    if (baseWorkStartTime && baseWorkEndTime && Number(pickNumber(baseWorkStartTime)) >= Number(pickNumber(baseWorkEndTime))) {
      tmpErrors.push({
        type: "work_time",
        id: selectNumber,
        message: "標準勤務時間の設定が誤っています。勤務終了時間は勤務開始時間より前の時間を指定してください。",
      })
    }
    if (isBaseWorkTime && (workTimeSeconds - baseBreakTime <= 0)) {
      tmpErrors.push({
        type: "work_time",
        id: selectNumber,
        message: "標準勤務時間の設定が誤っています。休憩予定時間は勤務時間以下にしてください。",
      })
    }
    scheduledBreakTimes.forEach((v, i) => {
      const startNum = Number(pickNumber(v.startTime))
      const endNum = Number(pickNumber(v.endTime))
      if (isScheduledBreakTimes && (endNum - startNum <= 0)) {
        tmpErrors.push({
          type: "break_time",
          id: selectNumber,
          message: "標準休憩時間の設定が誤っています。休憩予定終了時間は休憩予定開始時間より前の時間を指定してください。",
        })
      }
      scheduledBreakTimes.filter((_, j) => j > i).forEach((w) => {
        const tmpStartNum = Number(pickNumber(w.startTime))
        const tmpEndNum = Number(pickNumber(w.endTime))
        if (!(startNum >= tmpEndNum || endNum <= tmpStartNum)) {
          tmpErrors.push({
            type: "break_time",
            id: selectNumber,
            message: "標準休憩時間の設定が誤っています。設定時間が重複しています。",
          })
        }
      })
    })
    if (tmpErrors.length) {
      setErrors(tmpErrors)
    } else {
      setErrors([])
    }
  }, [targetTitle, targetMemberIds, baseWorkStartTime, baseWorkEndTime, baseBreakTime, scheduledBreakTimes, workTimeSeconds])

  useEffect(() => {
    if (!holidaySchedule.holidaySchedules?.length || holidaySchedule.isLoading) return
    setItems(
      holidaySchedule.holidaySchedules.map((v) => ({
        id: v.id,
        title: v.name,
        holidayEnabled: v.holiday_enabled,
        holidayList: v.holidays,
        memberIds: v.member_ids,
        common: v.common,
        baseWorkStartTime: v.base_work_start_time,
        baseWorkEndTime: v.base_work_end_time,
        baseBreakTime: v.base_break_time,
        scheduledBreakTimes: v.scheduled_break_times.map((w) => ({ startTime: w.start_time, endTime: w.end_time })),
        useCommonWorkTime: v.use_common_work_time,
        useCommonHolidayList: v.use_common_holiday_list,
        useCommonScheduledBreakTimes: v.use_common_scheduled_break_times,
      })),
    )
    setSelectNumber((cur) => cur || holidaySchedule.holidaySchedules?.find((v) => v.common)?.id || 0)
  }, [holidaySchedule])

  useEffect(() => {
    if (holidaySchedule.isLoading) return
    setTimeout(() => {
      setIsLoading(false)
    }, 1000)
  }, [holidaySchedule.isLoading])

  useEffect(() => {
    if (!items.length) return
    const target = items.find((v) => v.id === selectNumber) || itemInit
    setTargetTitle(target.title || "")
    setTargetMemberIds(target.memberIds)
    setHolidayList(
      Array(12).fill(null).map((_v, i) => (
        target.holidayList.filter((w) => Number(format(w, "M")) === i + 1)
      )),
    )
    setBaseWorkStartTime(timeAdjuster(target.baseWorkStartTime, true) || "")
    setBaseWorkEndTime(timeAdjuster(target.baseWorkEndTime, true) || "")
    setBaseBreakTime(target.baseBreakTime || 0)
    setIsBaseWorkTime(!!target.baseWorkStartTime && !!target.baseWorkEndTime)
    setScheduledBreakTimes(
      target.scheduledBreakTimes.length
        ? target.scheduledBreakTimes.map((v) => ({ startTime: timeAdjuster(v.startTime, true) || "", endTime: timeAdjuster(v.endTime, true) || "" }))
        : [{ startTime: "", endTime: "" }],
    )
    setIsScheduledBreakTimes(!!target.scheduledBreakTimes.length)
    setIsUseCommonWorkTime(target.useCommonWorkTime)
    setIsUseCommonHolidayList(target.useCommonHolidayList)
    setIsUseCommonScheduledBreakTimes(target.useCommonScheduledBreakTimes)
    setIsHoliday(target.holidayEnabled)
    setTimeout(() => {
      setIsChangeLoading(false)
    }, 2000)
  }, [selectNumber, items])

  useEffect(() => {
    if (!selectNumber) return
    dispatch(fetchHolidayScheduleDetail({ id: selectNumber }))
  }, [selectNumber])

  useEffect(() => {
    if (!isClear) return
    setIsClear(false)
  }, [isClear])

  useEffect(() => {
    if (!quickChange.length) return
    setQuickChange("")
  }, [quickChange])

  return (
    <>
      <section className="setting_page">
        <Helmet>
          <title>Qasee - 設定 - 共通設定 - 勤務時間・休日設定</title>
        </Helmet>
        <section className="breadcrumb">
          設定
          &nbsp;&gt;&nbsp;
          共通設定
          &nbsp;&gt;&nbsp;
          勤務時間・休日設定
          &nbsp;
        </section>
        <section className="page_header">
          <h1>
            勤務時間・休日設定
            <UserGuide
              minHeight="465px"
              defaultOpen={!current.settingHolidays}
              callback={() => dispatch(updateSettingHolidays(true))}
            >
              <>
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "24px",
                    fontWeight: "bold",
                    marginBottom: "16px",
                    color: "#395F99",
                  }}
                >
                  勤務時間・休日設定でQaseeをより便利に！
                </div>
                <div
                  style={{
                    width: "100%",
                    height: "300px",
                    backgroundColor: "#FAFAFA",
                    marginBottom: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src="/static/images/task/application/image_1.png"
                    alt="申請・承認機能で仕分けデータがそのまま勤怠データに"
                  />
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    color: "#666666",
                  }}
                >
                  <p>
                    勤務時間や会社の休日を設定することでQaseeをより便利に使うことができます！
                  </p>
                  <p>
                    勤務時間を設定すると、計測時間を限定したり、残業時間をより細かく判定することができます。
                  </p>
                  <p>
                    また、休日を設定すると、未計測アラートの対象外になり不要な通知に悩まされる必要がなくなります。
                  </p>
                  <p>
                    「カスタムアラート機能」や「工数管理機能」で休日の労働状態を把握することができます。
                  </p>
                </div>
              </>
              <>
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "24px",
                    fontWeight: "bold",
                    marginBottom: "16px",
                    color: "#395F99",
                  }}
                >
                  様々な働き方に対応！複数の休日パターンを設定できます！
                </div>
                <div
                  style={{
                    width: "100%",
                    height: "300px",
                    backgroundColor: "#FAFAFA",
                    marginBottom: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src="/static/images/task/application/image_2.png"
                    alt="ユーザーの負担を減らします"
                  />
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    color: "#666666",
                  }}
                >
                  <p>
                    部署や事業所の違いによって働き方が違うユーザーのいるため、単一の設定だけでは対応しきれないことがあります。。。
                  </p>
                  <p>
                    Qaseeでは複数の休日パターンや複数の標準勤務時間を設定することができます！
                  </p>
                  <p>
                    対象ユーザーを選択して、それぞれの働き方にあわせてQaseeを活用することができます。
                  </p>
                </div>
              </>
            </UserGuide>
          </h1>
        </section>
        <section className="page_description">
          <p>
            勤務開始・終了時間、休憩開始・終了時間を設定することができます。
          </p>
          <p>
            また、日付クリックで簡単に休日を設定することができます。
          </p>
          <p>
            グループを作成して全体と異なる勤務時間や休日を設定することもできます。
          </p>
        </section>
        <section className="page_contents content-area">
          {
            (isLoading || isChangeLoading) && (
              <div
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  zIndex: "2049",
                  padding: "160px 0",
                  userSelect: "none",
                }}
              >
                <LoadingSquare>
                  <>
                    Loading…
                  </>
                </LoadingSquare>
              </div>
            )
          }
          <div className="content-block">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "-1px",
                overflowX: "scroll",
                scrollbarWidth: "none",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "0 8px",
                }}
              >
                {
                  items.map((v, i) => (
                    <div
                      key={`tab_${i + 1}`}
                      style={{
                        position: "relative",
                        border: "solid 1px #CCCCCC",
                        borderBottom: selectNumber === v.id ? "solid 6px #FFFFFF" : "none",
                        borderRadius: "3px 3px 0 0",
                        padding: "8px 16px",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        marginBottom: selectNumber === v.id ? "-3px" : "0px",
                      }}
                      onClick={() => selectNumber !== v.id && changeSelectNumber(v.id)}
                      role="none"
                    >
                      {v.title}
                      {
                        !v.common && (
                          <span
                            style={{
                              marginLeft: "4px",
                              cursor: "pointer",
                            }}
                            onClick={(e) => {
                              openConfirmationModal(v.id || 0, v.title || "")
                              e.stopPropagation()
                            }}
                            role="none"
                          >
                            <FontAwesomeIcon icon={['fas', 'times']} />
                          </span>
                        )
                      }
                      {
                        !!errors.filter((w) => w.id === v.id).length && (
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              color: "#FF0000",
                            }}
                          >
                            <FontAwesomeIcon icon={['fas', 'ban']} />
                          </div>
                        )
                      }
                    </div>
                  ))
                }
                {
                  !!items.length && (
                    <div
                      style={{
                        border: "solid 1px #139d3f",
                        borderRadius: "3px 3px 0 0",
                        padding: "8px",
                        cursor: "pointer",
                        backgroundColor: "#efffe4",
                        color: "#139d3f",
                      }}
                      onClick={() => addCondition()}
                      role="none"
                    >
                      <FontAwesomeIcon icon={['fas', 'plus']} />
                    </div>
                  )
                }
              </div>
            </div>
            <div
              style={{
                border: "solid 1px #CCCCCC",
                borderRadius: "4px",
                backgroundColor: "#FFFFFF",
                padding: "16px",
                display: "flex",
                flexFlow: "column",
                gap: "8px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  {
                    isEditTitle ? (
                      <>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          設定名：
                          <input
                            type="text"
                            ref={titleRef}
                            defaultValue={targetTitle || ""}
                            style={{
                              width: "200px",
                            }}
                          />
                          <button
                            type="button"
                            onClick={saveTitle}
                            style={{
                              border: "1px solid #139d3f",
                              borderRadius: "4px",
                              padding: "6px",
                              cursor: "pointer",
                              backgroundColor: "#efffe4",
                              color: "#139d3f",
                            }}
                          >
                            完了
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div
                          role="none"
                          onClick={() => !!selectNumber && setIsEditTitle(true)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            cursor: selectNumber ? "pointer" : "initial",
                          }}
                        >
                          {
                            !items.find((v) => v.id === selectNumber)?.common ? (
                              <>
                                設定名：
                                <div
                                  style={{
                                    borderBottom: "dashed 1px #999999",
                                    padding: "2px 4px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  {targetTitle}
                                  <FontAwesomeIcon icon={['fas', 'pen']} />
                                </div>
                              </>
                            ) : (
                              <>
                                全体
                              </>
                            )
                          }
                        </div>
                      </>
                    )
                  }
                </div>
                <div>
                  <button
                    type="button"
                    className="button"
                    onClick={() => saveSetting()}
                    disabled={!!errors.length || isSubmitLoading}
                  >
                    {
                      isSubmitLoading ? (
                        <>
                          <FontAwesomeIcon icon={['fas', 'spinner']} spin />
                        </>
                      ) : (
                        <>
                          設定を保存する
                        </>
                      )
                    }
                  </button>
                </div>
              </div>
              {
                errors.filter((v) => v.type === "title" && v.id === selectNumber).map((v) => (
                  <div
                    key={uniqueId(`${selectNumber}`)}
                    style={{
                      border: "solid 1px #FF0000",
                      borderRadius: "4px",
                      color: "#FF0000",
                      backgroundColor: "#fff3f3",
                      padding: "8px",
                    }}
                  >
                    {v.message}
                  </div>
                ))
              }
              {
                !items.find((v) => v.id === selectNumber)?.common && (
                  <div
                    style={{
                      position: "relative",
                      zIndex: "2001",
                      border: "solid 1px #CCCCCC",
                      borderRadius: "4px",
                      backgroundColor: "#FFFFFF",
                      padding: "16px",
                      display: "flex",
                      flexFlow: "column",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        backgroundColor: "#f3f3f3",
                        padding: "8px",
                        margin: "-16px -16px 0 -16px",
                        borderRadius: "4px 4px 0 0",
                        marginBottom: "8px",
                      }}
                    >
                      対象メンバー
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: "4px",
                      }}
                    >
                      {
                        targetMemberIds.map((memberId) => (
                          <div
                            style={{
                              border: "solid 1px #CCCCCC",
                              borderRadius: "20px",
                              padding: "8px 8px",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              userSelect: "none",
                            }}
                            key={`member_id_${memberId}_${selectNumber}`}
                          >
                            {
                              teamList.full_members.find((v) => v.id === memberId)?.profile_image ? (
                                <img
                                  alt="profile"
                                  src={teamList.full_members.find((v) => v.id === memberId)?.profile_image || ""}
                                  className="profile_icon"
                                  style={{
                                    borderRadius: "50%",
                                    width: "14px",
                                  }}
                                />
                              ) : (
                                <img
                                  alt=""
                                  src={`/static/images/profile/${(Number(memberId) || 0) % 12}.jpeg`}
                                  className="profile_icon"
                                  style={{
                                    borderRadius: "50%",
                                    width: "14px",
                                  }}
                                />
                              )
                            }
                            {teamList.full_members.find((v) => v.id === memberId)?.name}
                            <span
                              style={{
                                marginLeft: "4px",
                                cursor: "pointer",
                              }}
                              onClick={() => ejectMemberId(memberId)}
                              role="none"
                            >
                              <FontAwesomeIcon icon={['fas', 'times']} />
                            </span>
                          </div>
                        ))
                      }
                      <AddMemberGroupingModule
                        memberIdList={targetMemberIds || []}
                        callbackMembers={(val) => selectMemberIds(val.map((v) => v.id))}
                        memberList={
                          teamList.full_members.map((v) => ({ id: v.id, name: v.name, profile_image: v.profile_image, active: v.active }))
                        }
                        teamList={teamList.teams}
                      />
                    </div>
                  </div>
                )
              }
              {
                errors.filter((v) => v.type === "member" && v.id === selectNumber).map((v) => (
                  <div
                    key={uniqueId(`${selectNumber}`)}
                    style={{
                      border: "solid 1px #FF0000",
                      borderRadius: "4px",
                      color: "#FF0000",
                      backgroundColor: "#fff3f3",
                      padding: "8px",
                    }}
                  >
                    <div>
                      {v.message}
                    </div>
                    <div>
                      {`対象メンバー：${v.target?.join(",")}`}
                    </div>
                  </div>
                ))
              }
              <div
                style={{
                  position: "relative",
                  border: "solid 1px #CCCCCC",
                  borderRadius: "4px",
                  backgroundColor: "#f3feff",
                  padding: "16px",
                  display: "flex",
                  flexFlow: "column",
                  gap: "8px",
                }}
              >
                {
                  isUseCommonWorkTime && (
                    <div
                      style={{
                        position: "absolute",
                        top: "28px",
                        left: 0,
                        width: "100%",
                        height: "calc(100% - 28px)",
                        backgroundColor: "rgba(243, 243, 243, 0.8)",
                        zIndex: "2000",
                      }}
                    />
                  )
                }
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    backgroundColor: "#f3f3f3",
                    padding: "8px",
                    margin: "-16px -16px 0 -16px",
                    borderRadius: "4px 4px 0 0",
                    marginBottom: "8px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    勤務時間設定
                  </div>
                  {
                    !items.find((v) => v.id === selectNumber)?.common && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "2px",
                          cursor: "pointer",
                          fontSize: "10px",
                          color: "#666666",
                        }}
                        onClick={() => changeUseCommon(setIsUseCommonWorkTime)}
                        role="none"
                      >
                        {buildCheckBox(isUseCommonWorkTime)}
                        全体設定に準拠する
                      </div>
                    )
                  }
                </div>
                <div
                  style={{
                    display: "flex",
                    flexFlow: "column",
                    gap: "4px",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        userSelect: "none",
                        cursor: "pointer",
                      }}
                      onClick={changeSettingBaseWorkTime}
                      role="none"
                    >
                      {buildCheckBox(isBaseWorkTime)}
                      標準勤務時間を設定する
                    </div>
                  </div>
                  <div
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "8px",
                      border: "solid 1px #CCCCCC",
                      borderRadius: "4px",
                      padding: "4px 8px",
                      backgroundColor: "#FFFFFF",
                      fontSize: "11px",
                      minHeight: "35px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      勤務開始
                      {buildWorkTimeSelectBox(baseWorkStartTime, (v) => setBaseWorkStartTime(v))}
                      ~
                      勤務終了
                      {buildWorkTimeSelectBox(baseWorkEndTime, (v) => setBaseWorkEndTime(v))}
                      うち休憩予定時間
                      {buildBreakTimeSelectBox(baseBreakTime, (v) => setBaseBreakTime(v))}
                    </div>
                    {
                      !isBaseWorkTime ? (
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            backgroundColor: "rgba(243, 243, 243, 0.8)",
                            borderRadius: "4px",
                            zIndex: "2000",
                          }}
                        />
                      ) : (
                        <>
                          {
                            !errors.filter((v) => v.type === "work_time" && v.id === selectNumber).length && (
                              <div
                                style={{
                                  display: "flex",
                                  gap: "4px",
                                  color: "#999999",
                                }}
                              >
                                標準勤務時間
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "4px",
                                  }}
                                >
                                  {buildSecToTime(baseBreakTime ? workTimeSeconds - baseBreakTime : workTimeSeconds)}
                                  {!!baseBreakTime && `(休憩予定時間:${buildSecToTime(baseBreakTime)})`}
                                </div>
                              </div>
                            )
                          }
                        </>
                      )
                    }
                  </div>
                </div>
                {
                  errors.filter((v) => v.type === "work_time" && v.id === selectNumber).map((v) => (
                    <div
                      key={uniqueId(`${selectNumber}`)}
                      style={{
                        border: "solid 1px #FF0000",
                        borderRadius: "4px",
                        color: "#FF0000",
                        backgroundColor: "#fff3f3",
                        padding: "8px",
                      }}
                    >
                      {v.message}
                    </div>
                  ))
                }
              </div>
              <div
                style={{
                  position: "relative",
                  border: "solid 1px #CCCCCC",
                  borderRadius: "4px",
                  backgroundColor: "#f3fff4",
                  padding: "16px",
                  display: "flex",
                  flexFlow: "column",
                  gap: "8px",
                }}
              >
                {
                  isUseCommonScheduledBreakTimes && (
                    <div
                      style={{
                        position: "absolute",
                        top: "28px",
                        left: 0,
                        width: "100%",
                        height: "calc(100% - 28px)",
                        backgroundColor: "rgba(243, 243, 243, 0.8)",
                        zIndex: "2000",
                      }}
                    />
                  )
                }
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    backgroundColor: "#f3f3f3",
                    padding: "8px",
                    margin: "-16px -16px 0 -16px",
                    borderRadius: "4px 4px 0 0",
                    marginBottom: "8px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    休憩時間をあらかじめ登録する
                  </div>
                  {
                    !items.find((v) => v.id === selectNumber)?.common && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "2px",
                          cursor: "pointer",
                          fontSize: "10px",
                          color: "#666666",
                        }}
                        onClick={() => changeUseCommon(setIsUseCommonScheduledBreakTimes)}
                        role="none"
                      >
                        {buildCheckBox(isUseCommonScheduledBreakTimes)}
                        全体設定に準拠する
                      </div>
                    )
                  }
                </div>
                <div
                  style={{
                    display: "flex",
                    flexFlow: "column",
                    gap: "4px",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        userSelect: "none",
                        cursor: "pointer",
                      }}
                      onClick={changeSettingScheduledBreakTime}
                      role="none"
                    >
                      {buildCheckBox(isScheduledBreakTimes)}
                      休憩時間をあらかじめ登録する
                    </div>
                  </div>
                  <div
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "8px",
                      border: "solid 1px #CCCCCC",
                      borderRadius: "4px",
                      padding: "4px 8px",
                      backgroundColor: "#FFFFFF",
                      fontSize: "11px",
                      minHeight: "35px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexFlow: "column",
                          gap: "4px",
                        }}
                      >
                        {
                          scheduledBreakTimes.map((v, i) => (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              休憩開始
                              { /* eslint-disable-next-line max-len */}
                              {buildWorkTimeSelectBox(v.startTime, (value) => setScheduledBreakTimes((cur) => cur.map((w, j) => (i === j ? { ...w, startTime: value } : w))))}
                              ~
                              休憩終了
                              { /* eslint-disable-next-line max-len */}
                              {buildWorkTimeSelectBox(v.endTime, (value) => setScheduledBreakTimes((cur) => cur.map((w, j) => (i === j ? { ...w, endTime: value } : w))))}
                              {
                                !!v.startTime && (
                                  <button
                                    type="button"
                                    onClick={() => delBreakTime(i)}
                                    style={{
                                      color: "#FF0000",
                                    }}
                                  >
                                    <FontAwesomeIcon icon={['fas', 'trash']} />
                                  </button>
                                )
                              }
                            </div>
                          ))
                        }
                      </div>
                    </div>
                    {
                      !isScheduledBreakTimes ? (
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            backgroundColor: "rgba(243, 243, 243, 0.8)",
                            borderRadius: "4px",
                            zIndex: "2000",
                          }}
                        />
                      ) : (
                        <>
                          {
                            !errors.filter((v) => v.type === "break_time" && v.id === selectNumber).length && (
                              <div
                                style={{
                                  display: "flex",
                                  gap: "4px",
                                  color: "#999999",
                                }}
                              >
                                合計
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "4px",
                                  }}
                                >
                                  {buildSecToTime(scheduledBreakTimeSeconds)}
                                </div>
                              </div>
                            )
                          }
                        </>
                      )
                    }
                  </div>
                  {
                    isScheduledBreakTimes && scheduledBreakTimes.length < BREAK_TIME_LIMIT && (
                      <button
                        type="button"
                        onClick={addBreakTime}
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          gap: "4px",
                          border: "solid 1px #008800",
                          borderRadius: "4px",
                          color: "#008800",
                          backgroundColor: "#e1ffe0",
                        }}
                      >
                        <FontAwesomeIcon icon={['fas', 'plus']} />
                        休憩時間を追加
                      </button>
                    )
                  }
                </div>
                {
                  errors.filter((v) => v.type === "break_time" && v.id === selectNumber).map((v) => (
                    <div
                      key={uniqueId(`${selectNumber}`)}
                      style={{
                        border: "solid 1px #FF0000",
                        borderRadius: "4px",
                        color: "#FF0000",
                        backgroundColor: "#fff3f3",
                        padding: "8px",
                      }}
                    >
                      {v.message}
                    </div>
                  ))
                }
              </div>
              <div
                style={{
                  position: "relative",
                  border: "solid 1px #CCCCCC",
                  borderRadius: "4px",
                  backgroundColor: "#fffafa",
                  padding: "16px",
                  display: "flex",
                  flexFlow: "column",
                  gap: "8px",
                }}
              >
                {
                  isUseCommonHolidayList && (
                    <div
                      style={{
                        position: "absolute",
                        top: "28px",
                        left: 0,
                        width: "100%",
                        height: "calc(100% - 28px)",
                        backgroundColor: "rgba(243, 243, 243, 0.8)",
                        zIndex: "2000",
                      }}
                    />
                  )
                }
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    backgroundColor: "#f3f3f3",
                    padding: "8px",
                    margin: "-16px -16px 0 -16px",
                    borderRadius: "4px 4px 0 0",
                    marginBottom: "8px",
                  }}
                >
                  <div>
                    休日設定
                  </div>
                  {
                    !items.find((v) => v.id === selectNumber)?.common && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "2px",
                          cursor: "pointer",
                          fontSize: "10px",
                          color: "#666666",
                        }}
                        onClick={() => changeUseCommon(setIsUseCommonHolidayList)}
                        role="none"
                      >
                        {buildCheckBox(isUseCommonHolidayList)}
                        全体設定に準拠する
                      </div>
                    )
                  }
                </div>
                <div
                  style={{
                    display: "flex",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      userSelect: "none",
                      cursor: "pointer",
                    }}
                    onClick={changeSettingHoliday}
                    role="none"
                  >
                    {buildCheckBox(isHoliday)}
                    休日を設定する
                  </div>
                </div>
                {
                  isHoliday && (
                    <>
                      <div
                        style={{
                          border: "solid 1px #FF0000",
                          borderRadius: "4px",
                          padding: "8px",
                          color: "#FF0000",
                          backgroundColor: "#ffeeee",
                        }}
                      >
                        ※ 休日設定は計測時間を制限するものではありません。計測時間の設定は
                        <Link
                          to={setPath(URL.SETTING_MEASUREMENT.path, "ja")}
                          className=""
                        >
                          こちら
                        </Link>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexFlow: "column",
                          gap: "8px",
                        }}
                      >
                        {buildQuickHolidaySettingBox()}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "16px",
                            position: "relative",
                          }}
                        >
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              borderRadius: "4px",
                              backgroundColor: "#fffafa",
                              display: "flex",
                              alignItems: "center",
                              fontSize: "10px",
                              padding: "8px",
                            }}
                          >
                            <div
                              style={{
                                backgroundColor: "#FF0000",
                                width: "12px",
                                height: "12px",
                                borderRadius: "50%",
                                opacity: "0.2",
                              }}
                            />
                            ：休日
                          </div>
                          <button
                            type="button"
                            onClick={() => changeTargetYear(targetYear - 1)}
                          >
                            <FontAwesomeIcon icon={['fas', 'angle-left']} />
                          </button>
                          <div>
                            {`${targetYear}年`}
                          </div>
                          <button
                            type="button"
                            onClick={() => changeTargetYear(targetYear + 1)}
                          >
                            <FontAwesomeIcon icon={['fas', 'angle-right']} />
                          </button>
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              right: 0,
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <button
                              type="button"
                              onClick={clearNotWorkdayList}
                              style={{
                                border: "solid 1px #CCCCCC",
                                borderRadius: "4px",
                                backgroundColor: "#FFFFFF",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                fontSize: "12px",
                              }}
                            >
                              <FontAwesomeIcon icon={['fas', 'ban']} />
                              {`${targetYear}年の休日をすべて解除`}
                            </button>
                            {
                              !items.find((v) => v.id === selectNumber)?.common && (
                                <button
                                  type="button"
                                  onClick={copyCommonHolidays}
                                  style={{
                                    border: "solid 1px #CCCCCC",
                                    borderRadius: "4px",
                                    backgroundColor: "#FFFFFF",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    fontSize: "12px",
                                  }}
                                >
                                  <FontAwesomeIcon icon={['fas', 'copy']} />
                                  全体設定の休日をコピー
                                </button>
                              )
                            }
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "8px",
                            zIndex: "1999",
                          }}
                        >
                          {
                            Array(12).fill(null).map((_v, i) => (
                              <>
                                <MonthlyCalendar
                                  targetYear={targetYear}
                                  targetMonth={i + 1}
                                  holidayList={holidayList[i]}
                                  submitCallback={(addList, removeList) => changeHoliday(addList, removeList)}
                                  isSubmit={isSubmit}
                                  isClear={isClear}
                                  quickChange={quickChange}
                                  isEditCallback={() => setIsEdit(true)}
                                />
                              </>
                            ))
                          }
                        </div>
                      </div>
                    </>
                  )
                }
              </div>
              {
                !!errors.length && (
                  <div
                    style={{
                      border: "solid 1px #FF0000",
                      borderRadius: "4px",
                      color: "#FF0000",
                      backgroundColor: "#fff3f3",
                      padding: "8px",
                      marginTop: "16px",
                    }}
                  >
                    エラーになっている項目があります。
                  </div>
                )
              }
              <div
                style={{
                  textAlign: "center",
                  padding: "8px",
                  marginTop: "16px",
                }}
              >
                <button
                  type="button"
                  className="button"
                  onClick={() => saveSetting()}
                  disabled={!!errors.length || isSubmitLoading}
                >
                  設定を保存する
                </button>
              </div>
            </div>
          </div>
        </section>
      </section>
      <Prompt
        when={isEdit}
        message="このページから移動しますか？行った変更が保存されない可能性があります。"
      />
      <ConfirmationModal
        confirmationModalState={confirmationModalState}
        closeCallback={closeConfirmationModal}
      />
      <Modal
        show={isDisplayInfo}
        closeCallback={closeInfoModal}
        modalSize="large"
      >
        <>
          <div
            style={{
              display: "flex",
              flexFlow: "column",
              gap: "16px",
              fontSize: "14px",
            }}
          >
            {
              modalType === "baseWorkTime" && (
                <>
                  <div
                    style={{
                      fontSize: "20px",
                      fontWeight: "bold",
                      color: "#666666",
                      textAlign: "center",
                      marginBottom: "24px",
                    }}
                  >
                    標準勤務時間
                  </div>
                  <div>
                    勤務の標準開始時刻・標準終了時刻を設定して、適切な勤務時間設定と時間外作業の可視化を可能にします。
                  </div>
                  <div
                    style={{
                      border: "solid 1px #CCCCCC",
                      borderRadius: "4px",
                    }}
                  >
                    <div
                      style={{
                        padding: "8px",
                        backgroundColor: "#EEEEEE",
                        borderRadius: "4px 4px 0 0",
                        textAlign: "center",
                        fontSize: "16px",
                        fontWeight: "bold",
                        color: "#666666",
                      }}
                    >
                      カスタムアラートでの残業時間判定
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexFlow: "column",
                        gap: "8px",
                        padding: "8px",
                        borderRadius: "0 0 4px 4px",
                      }}
                    >
                      <img
                        src="/static/images/holiday_schedule/image_1.png"
                        alt="残業時間の判定がより細かく正確に"
                        style={{
                          margin: "0 auto",
                        }}
                      />
                      <div>
                        標準勤務時間を設定すると、
                        <span style={{ fontWeight: "bold" }}>残業時間の判定がより細かく正確</span>
                        になります。
                      </div>
                      <div>
                        カスタムアラートで残業時間判定をされる方にオススメの設定です。
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      border: "solid 1px #CCCCCC",
                      borderRadius: "4px",
                    }}
                  >
                    <div
                      style={{
                        padding: "8px",
                        backgroundColor: "#EEEEEE",
                        borderRadius: "4px 4px 0 0",
                        textAlign: "center",
                        fontSize: "16px",
                        fontWeight: "bold",
                        color: "#666666",
                      }}
                    >
                      工数管理での標準勤務時間の表示・残業時間判定
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexFlow: "column",
                        gap: "8px",
                        padding: "8px",
                        borderRadius: "0 0 4px 4px",
                      }}
                    >
                      <img
                        src="/static/images/holiday_schedule/image_2.png"
                        alt="仕分け時の把握が楽に便利"
                        style={{
                          margin: "0 auto",
                        }}
                      />
                      <div>
                        標準勤務時間を設定することで、
                        <span style={{ fontWeight: "bold" }}>時間外の作業内容が視覚的にわかりやすく把握できる</span>
                        ようになります。
                      </div>
                      <div>
                        また、
                        <span style={{ fontWeight: "bold" }}>残業時間の判定がより細かく正確</span>
                        になります。
                      </div>
                    </div>
                  </div>
                </>
              )
            }
            {
              modalType === "scheduledBreakTime" && (
                <>
                  <div
                    style={{
                      fontSize: "20px",
                      fontWeight: "bold",
                      color: "#666666",
                      textAlign: "center",
                      marginBottom: "24px",
                    }}
                  >
                    休憩時間をあらかじめ登録する
                  </div>
                  <div
                    style={{
                      border: "solid 1px #CCCCCC",
                      borderRadius: "4px",
                    }}
                  >
                    <div
                      style={{
                        padding: "8px",
                        backgroundColor: "#EEEEEE",
                        borderRadius: "4px 4px 0 0",
                        textAlign: "center",
                        fontSize: "16px",
                        fontWeight: "bold",
                        color: "#666666",
                      }}
                    >
                      工数管理での休憩時間作業データを自動で作成
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexFlow: "column",
                        gap: "8px",
                        padding: "8px",
                        borderRadius: "0 0 4px 4px",
                      }}
                    >
                      <img
                        src="/static/images/holiday_schedule/image_4.png"
                        alt="工数管理での休憩時間作業データを自動で作成"
                        style={{
                          margin: "0 auto",
                        }}
                      />
                      <div>
                        「休憩時間をあらかじめ登録する」をオンにすると、
                        <span style={{ fontWeight: "bold" }}>設定した時刻に休憩の作業データを自動設定</span>
                        します。
                      </div>
                      <div>
                        休憩時間作業データ作成の手間を減らして、工数管理の自動化を実現します。
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      color: "#FF0000",
                      fontSize: "12px",
                    }}
                  >
                    <div>
                      ※設定反映は翌日以降の作業データが対象になります。
                    </div>
                    <div>
                      ※休日設定されていない場合、平日のみ休憩時間をあらかじめ登録します。
                    </div>
                    <div>
                      ※休日設定されている場合、休日には休憩時間をあらかじめ登録しません。
                    </div>
                    <div>
                      ※休憩の作業データが自動設定された時刻は通常の作業データは作成されません。あらかじめご了承ください。
                    </div>
                  </div>
                </>
              )
            }
            {
              modalType === "holiday" && (
                <>
                  <div
                    style={{
                      fontSize: "20px",
                      fontWeight: "bold",
                      color: "#666666",
                      textAlign: "center",
                      marginBottom: "24px",
                    }}
                  >
                    休日設定
                  </div>
                  <div>
                    会社の休日を設定して、勤務日以外の通知を制限したり、各種データの最適化を実現します。
                  </div>
                  <div
                    style={{
                      border: "solid 1px #FF0000",
                      borderRadius: "4px",
                      padding: "8px",
                      color: "#FF0000",
                      backgroundColor: "#ffeeee",
                    }}
                  >
                    ※ 休日設定は計測時間を制限するものではありません。計測時間の設定は
                    <Link
                      to={setPath(URL.SETTING_MEASUREMENT.path, "ja")}
                      className=""
                    >
                      こちら
                    </Link>
                  </div>
                  <div
                    style={{
                      border: "solid 1px #CCCCCC",
                      borderRadius: "4px",
                    }}
                  >
                    <div
                      style={{
                        padding: "8px",
                        backgroundColor: "#EEEEEE",
                        borderRadius: "4px 4px 0 0",
                        textAlign: "center",
                        fontSize: "16px",
                        fontWeight: "bold",
                        color: "#666666",
                      }}
                    >
                      通知メールの対象外に
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexFlow: "column",
                        gap: "8px",
                        padding: "8px",
                        borderRadius: "0 0 4px 4px",
                      }}
                    >
                      <img
                        src="/static/images/holiday_schedule/image_5.png"
                        alt="通知メールの対象外に"
                        style={{
                          margin: "0 auto",
                        }}
                      />
                      <div>
                        休日を設定すると、
                        <span style={{ fontWeight: "bold" }}>未計測アラートの対象外になり不要な通知に悩まされる必要がなくなります</span>
                        。
                      </div>
                      <div>
                        連続3日以上の休暇がある場合は、設定することをオススメします。
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      border: "solid 1px #CCCCCC",
                      borderRadius: "4px",
                    }}
                  >
                    <div
                      style={{
                        padding: "8px",
                        backgroundColor: "#EEEEEE",
                        borderRadius: "4px 4px 0 0",
                        textAlign: "center",
                        fontSize: "16px",
                        fontWeight: "bold",
                        color: "#666666",
                      }}
                    >
                      カスタムアラートでの休日労働判定
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexFlow: "column",
                        gap: "8px",
                        padding: "8px",
                        borderRadius: "0 0 4px 4px",
                      }}
                    >
                      <img
                        src="/static/images/holiday_schedule/image_6.png"
                        alt="カスタムアラートでの休日労働判定"
                        style={{
                          margin: "0 auto",
                        }}
                      />
                      <div>
                        休日を設定すると、
                        <span style={{ fontWeight: "bold" }}>休日の労働状態を把握することが可能</span>
                        になります。
                      </div>
                      <div>
                        カスタムアラートで休日労働を確認したい方にオススメの設定です。
                      </div>
                    </div>
                  </div>
                </>
              )
            }
          </div>
        </>
        <>
          <div
            style={{
              display: "flex",
              flexFlow: "column",
              gap: "8px",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <button
              type="button"
              onClick={closeInfoModal}
              style={{
                textAlign: "center",
                minWidth: "200px",
                backgroundColor: "#4472BA",
                border: "1px solid #4472BA",
                color: "#FFFFFF",
                padding: "8px 12px",
                borderRadius: "4px",
              }}
            >
              わかりました！
            </button>
          </div>
        </>
      </Modal>
    </>
  )
}

export default Index
